import { useState, useEffect } from "react";
import OrderTables from "./OrderTables";
import useSelectedDomain from "../../../service/Hooks/useSelectedDomain";
import { getOrderCountByTarget, getOrderSideCounts } from "../../../service/api/Order";
import { Spinner } from "react-bootstrap";
import "../style/count.css";
import { formatAmount } from "../../../service/utils/orderUtils";

const OrderTable = ({ ordre }) => {
  const selectedDomain = useSelectedDomain();

  const [orders, setOrders] = useState([]);
  const [totalOrders, setTotalOrders] = useState(0);

  const [sideCounts, setSideCounts] = useState({ nbreAchat: 0, nbreVente: 0 });

  const [loading, setLoading] = useState(true);
  const [sideCountsLoading, setSideCountsLoading] = useState(true);

  const [error, setError] = useState("");
  const [sideCountsError, setSideCountsError] = useState("");

  // 1) Premier appel API pour les compteurs par marché
  useEffect(() => {
    if (!selectedDomain?.id) return;

    const fetchData = async () => {
      try {
        setLoading(true);
        setError("");

        const data = await getOrderCountByTarget(selectedDomain.id);

        if (Array.isArray(data) && data.length > 0) {
          setOrders(data);
          const total = data.reduce((sum, item) => sum + (item?.count ?? 0), 0);
          setTotalOrders(total);
        } else {
          setOrders([]);
          setTotalOrders(0);
        }
      } catch (err) {
        console.error("getOrderCountByTarget error:", err);
        setError(err?.message ?? "Erreur inconnue");
        setOrders([]);
        setTotalOrders(0);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedDomain?.id]);

  // 2) Deuxième appel API pour Achat/Vente
  useEffect(() => {
    if (!selectedDomain?.id) return;

    const fetchSideCounts = async () => {
      try {
        setSideCountsLoading(true);
        setSideCountsError("");

        // Reset pendant chargement
        setSideCounts({ nbreAchat: 0, nbreVente: 0 });

        const data = await getOrderSideCounts(selectedDomain.id);

        // 🔥 Sécurité totale (même si data = undefined)
        setSideCounts({
          nbreAchat: data?.nbreAchat ?? 0,
          nbreVente: data?.nbreVente ?? 0,
        });
      } catch (err) {
        console.error("getOrderSideCounts error:", err);
        setSideCountsError(err?.message ?? "Erreur lors du chargement");
        setSideCounts({ nbreAchat: 0, nbreVente: 0 });
      } finally {
        setSideCountsLoading(false);
      }
    };

    fetchSideCounts();
  }, [selectedDomain?.id]);

  return (
    <>
      <div className="order-container">
        <div className="order-grid">

          {/* Carte Achat */}
          <div className="card order-card buy-card">
            <div className="card-body d-flex align-items-center justify-content-between p-4">
              <div className="d-flex align-items-center">
                <div className="card-box-icon me-3 me-xl-4">
                  {sideCountsLoading ? (
                    <div className="d-flex align-items-center justify-content-center">
                      <Spinner animation="border" variant="success" size="sm" />
                    </div>
                  ) : sideCountsError ? (
                    <svg width="32" height="32" viewBox="0 0 28 30" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="14" cy="15" r="12" stroke="#EF4444" strokeWidth="2" fill="#FEE2E2" />
                      <path d="M14 10V20M10 15H18" stroke="#EF4444" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                  ) : (
                    <svg width="32" height="32" viewBox="0 0 28 30" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="14" cy="15" r="12" stroke="#10B981" strokeWidth="2" fill="#D1FAE5" />
                      <path d="M14 8V22M8 15H20" stroke="#10B981" strokeWidth="2" strokeLinecap="round" />
                      <path d="M10 12L14 8L18 12" stroke="#10B981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </div>

                <div>
                  <p className="mb-1 text-muted card-label">Ordre Achat</p>

                  {sideCountsLoading ? (
                    <Spinner animation="border" variant="success" size="sm" />
                  ) : sideCountsError ? (
                    <p className="text-danger mb-0 small">Erreur</p>
                  ) : (
                    <h2 className="mb-0 text-nowrap order-count text-success">
                      {formatAmount(sideCounts?.nbreAchat ?? 0)}
                    </h2>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Carte Vente */}
          <div className="card order-card sell-card">
            <div className="card-body d-flex align-items-center justify-content-between p-4">
              <div className="d-flex align-items-center">
                <div className="card-box-icon me-3 me-xl-4">
                  {sideCountsLoading ? (
                    <div className="d-flex align-items-center justify-content-center">
                      <Spinner animation="border" variant="danger" size="sm" />
                    </div>
                  ) : sideCountsError ? (
                    <svg width="32" height="32" viewBox="0 0 28 30" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="14" cy="15" r="12" stroke="#EF4444" strokeWidth="2" fill="#FEE2E2" />
                      <path d="M14 10V20M10 15H18" stroke="#EF4444" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                  ) : (
                    <svg width="32" height="32" viewBox="0 0 28 30" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="14" cy="15" r="12" stroke="#EF4444" strokeWidth="2" fill="#FEE2E2" />
                      <path d="M14 8V22M8 15H20" stroke="#EF4444" strokeWidth="2" strokeLinecap="round" />
                      <path d="M10 18L14 22L18 18" stroke="#EF4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </div>

                <div>
                  <p className="mb-1 text-muted card-label">Ordre Vente</p>

                  {sideCountsLoading ? (
                    <Spinner animation="border" variant="danger" size="sm" />
                  ) : sideCountsError ? (
                    <p className="text-danger mb-0 small">Erreur</p>
                  ) : (
                    <h2 className="mb-0 text-nowrap order-count text-danger">
                      {formatAmount(sideCounts?.nbreVente ?? 0)}
                    </h2>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Carte Total */}
          <div className="card order-card total-card">
            <div className="card-body d-flex align-items-center justify-content-between p-4">
              <div className="d-flex align-items-center">
                <div className="card-box-icon me-3 me-xl-4">
                  <svg width="32" height="32" viewBox="0 0 28 30" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="14" cy="15" r="12" stroke="#4C8BF5" strokeWidth="2" fill="#E3F2FD" />
                    <path d="M9 15H19M14 10V20" stroke="#4C8BF5" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                </div>
                <div>
                  <p className="mb-1 text-muted card-label">Total Ordres</p>
                  <h2 className="mb-0 text-nowrap order-count text-primary">
                    {formatAmount(totalOrders)}
                  </h2>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>

      <OrderTables />
    </>
  );
};

export default OrderTable;
